import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { ParametresLayout } from '@/components/layout/ParametresLayout';
import { EcheancesLayout } from '@/components/layout/EcheancesLayout';
import { CrmLayout } from '@/components/layout/CrmLayout';
import { FormationLayout } from '@/components/layout/FormationLayout';
import { FormationUnifiedLayout } from '@/components/layout/FormationUnifiedLayout';
import { FormationPage } from '@/routes/formation/FormationPage';
import { ReportingPage } from '@/routes/ReportingPage';
import { CommunicationPage } from '@/routes/CommunicationPage';
import { FinancesCaisseLayout } from '@/components/layout/FinancesCaisseLayout';
import { FormationDashboardPage } from '@/routes/formation/FormationDashboardPage';
import { SimulateurB2bPage } from '@/routes/formation/SimulateurB2bPage';
import { CataloguePage } from '@/routes/formation/CataloguePage';
import { FicheFormationPage } from '@/routes/formation/FicheFormationPage';
import { SessionsPage } from '@/routes/formation/SessionsPage';
import { SessionDetailPage } from '@/routes/formation/SessionDetailPage';
import { InscriptionsPage } from '@/routes/formation/InscriptionsPage';
import { FormateursPage } from '@/routes/formation/FormateursPage';
import { EvaluationsPage } from '@/routes/formation/EvaluationsPage';
import { RapportsPage } from '@/routes/formation/RapportsPage';
import { DossierDetailPage } from '@/routes/dossiers/DossierDetailPage';
import { B2cActionDetailPage } from '@/routes/formation-b2c/B2cActionDetailPage';
import { EvaluationPubliquePage } from '@/routes/EvaluationPubliquePage';
import { LoginPage } from '@/routes/LoginPage';
import { LogoutPage } from '@/routes/LogoutPage';
import { ForbiddenPage } from '@/routes/ForbiddenPage';
import { NotFoundPage } from '@/routes/NotFoundPage';
import { DashboardPage } from '@/routes/DashboardPage';
import { ModulePlaceholder } from '@/routes/ModulePlaceholder';
import { MarketingB2cPage } from '@/routes/marketing/MarketingB2cPage';
import { TestDataTablePage } from '@/routes/TestDataTablePage';
import { OrganisationPage } from '@/routes/parametres/OrganisationPage';
import { ActivitesPage } from '@/routes/parametres/ActivitesPage';
import { ActivitesGlobalPage } from '@/routes/parametres/ActivitesGlobalPage';
import { ServicesPage } from '@/routes/parametres/ServicesPage';
import { TiersPage } from '@/routes/parametres/TiersPage';
import { TierDetailPage } from '@/routes/parametres/TierDetailPage';
import { UserPermissionsPage } from '@/routes/parametres/UserPermissionsPage';
import { BanquesPage } from '@/routes/parametres/BanquesPage';
import { PlanComptablePage } from '@/routes/parametres/PlanComptablePage';
import { WorkflowsPage } from '@/routes/parametres/WorkflowsPage';
import { UtilisateursPage } from '@/routes/parametres/UtilisateursPage';
import { PerimetreUserPage } from '@/routes/parametres/PerimetreUserPage';
import { AuditPage } from '@/routes/parametres/AuditPage';
import { AdminPage } from '@/routes/parametres/AdminPage';
import { DoublonsTiersPage } from '@/routes/parametres/DoublonsTiersPage';
import { BackupsPage } from '@/routes/parametres/BackupsPage';
import { OperationsPage } from '@/routes/finances/OperationsPage';
import { TresoreriePage } from '@/routes/finances/TresoreriePage';
import { JustificatifsPage } from '@/routes/finances/JustificatifsPage';
import { CloturePage } from '@/routes/finances/CloturePage';
import { RapprochementPage } from '@/routes/finances/RapprochementPage';
import { ComptesPage } from '@/routes/finances/ComptesPage';
import { EcheanciersTab } from '@/routes/finances/EcheanciersTab';
import { AvancesInterActivitesTab } from '@/routes/finances/AvancesInterActivitesTab';
import { ChargesRecurrentesTab } from '@/routes/finances/ChargesRecurrentesTab';
import { ConsolideFormateursPage } from '@/routes/finances/ConsolideFormateursPage';
import { DettesFournisseursPage } from '@/routes/finances/DettesFournisseursPage';
import { RecouvrementEntreprisesPage } from '@/routes/finances/RecouvrementEntreprisesPage';
import { AEncaisserPage } from '@/routes/echeances/AEncaisserPage';
import { APayerPage } from '@/routes/echeances/APayerPage';
import { RelancesPage } from '@/routes/echeances/RelancesPage';
import { PlansPaiementPage } from '@/routes/echeances/PlansPaiementPage';
import { CashflowPage } from '@/routes/echeances/CashflowPage';
import { CrmDashboardPage } from '@/routes/crm/CrmDashboardPage';
import { CrmKanbanPage } from '@/routes/crm/CrmKanbanPage';
import { CrmListePage } from '@/routes/crm/CrmListePage';
import { CrmForecastingPage } from '@/routes/crm/CrmForecastingPage';
import { CrmAgendaPage } from '@/routes/crm/CrmAgendaPage';
import { OpportuniteDetailPage } from '@/routes/crm/OpportuniteDetailPage';
import { PipelinesAdminPage } from '@/routes/parametres/crm/PipelinesAdminPage';
import type { ModuleKey } from '@/hooks/usePermissions';

function shell(module: ModuleKey, child: React.ReactNode): React.ReactElement {
  return (
    <ProtectedRoute module={module}>
      <AppShell>{child}</AppShell>
    </ProtectedRoute>
  );
}

export default function App(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/403" element={<ForbiddenPage />} />

      <Route path="/" element={shell('dashboard', <DashboardPage />)} />

      {/* Sprint 6.6 — Module Caisse unifié (1 entrée sidebar + 6 onglets internes) */}
      <Route path="/finances/caisse" element={shell('finances', <FinancesCaisseLayout />)}>
        <Route index element={<Navigate to="/finances/caisse/operations" replace />} />
        <Route path="operations" element={<OperationsPage />} />
        <Route path="tresorerie" element={<TresoreriePage />} />
        <Route path="justificatifs" element={<JustificatifsPage />} />
        <Route path="cloture" element={<CloturePage />} />
        <Route path="rapprochement" element={<RapprochementPage />} />
        <Route path="comptes" element={<ComptesPage />} />
        {/* Sprint 6.7 — 3 nouveaux onglets Caisse Groupe */}
        <Route path="echeanciers" element={<EcheanciersTab />} />
        <Route path="avances" element={<AvancesInterActivitesTab />} />
        <Route path="charges-recurrentes" element={<ChargesRecurrentesTab />} />
      </Route>
      {/* Sprint 6.9.1 — Consolidés (sous-menu Finances expandable) */}
      <Route
        path="/finances/consolides"
        element={<Navigate to="/finances/consolides/formateurs" replace />}
      />
      <Route
        path="/finances/consolides/formateurs"
        element={shell('finances', <ConsolideFormateursPage />)}
      />
      <Route
        path="/finances/consolides/fournisseurs"
        element={shell('finances', <DettesFournisseursPage />)}
      />
      <Route
        path="/finances/consolides/recouvrement"
        element={shell('finances', <RecouvrementEntreprisesPage />)}
      />
      {/* Backward compat Sprint 2 → redirige toutes les anciennes routes /finances/* */}
      <Route path="/finances" element={<Navigate to="/finances/caisse/operations" replace />} />
      <Route
        path="/finances/operations"
        element={<Navigate to="/finances/caisse/operations" replace />}
      />
      <Route
        path="/finances/tresorerie"
        element={<Navigate to="/finances/caisse/tresorerie" replace />}
      />
      <Route
        path="/finances/justificatifs"
        element={<Navigate to="/finances/caisse/justificatifs" replace />}
      />
      <Route
        path="/finances/cloture"
        element={<Navigate to="/finances/caisse/cloture" replace />}
      />
      <Route
        path="/finances/rapprochement"
        element={<Navigate to="/finances/caisse/rapprochement" replace />}
      />
      <Route
        path="/finances/comptes"
        element={<Navigate to="/finances/caisse/comptes" replace />}
      />
      <Route path="/echeances" element={shell('echeances', <EcheancesLayout />)}>
        <Route index element={<Navigate to="/echeances/a-encaisser" replace />} />
        <Route path="a-encaisser" element={<AEncaisserPage />} />
        <Route path="a-payer" element={<APayerPage />} />
        <Route path="relances" element={<RelancesPage />} />
        <Route path="plans" element={<PlansPaiementPage />} />
        <Route path="cashflow" element={<CashflowPage />} />
      </Route>
      <Route path="/crm" element={shell('crm', <CrmLayout />)}>
        <Route index element={<CrmDashboardPage />} />
        <Route path="kanban" element={<CrmKanbanPage />} />
        <Route path="liste" element={<CrmListePage />} />
        <Route path="forecasting" element={<CrmForecastingPage />} />
        <Route path="agenda" element={<CrmAgendaPage />} />
      </Route>
      <Route path="/crm/opportunite/:id" element={shell('crm', <OpportuniteDetailPage />)} />
      {/* Sprint 6.0.1 — Formation = UN SEUL module avec segmented B2B/B2C + tabs internes */}
      <Route path="/formation/simulateur-b2b" element={shell('formation', <SimulateurB2bPage />)} />
      <Route path="/formation" element={shell('formation', <FormationUnifiedLayout />)}>
        {/* Page principale dispatch via search params ?segment=b2b|b2c & ?tab=... */}
        <Route index element={<FormationPage />} />
        {/* Vue 360° par session (Sprint 6 DossierDetailPage avec simulateur live) */}
        <Route path="sessions/:id" element={<DossierDetailPage />} />
        {/* Détail action B2C (participants + ROI action) */}
        <Route path="b2c/sessions/:id" element={<B2cActionDetailPage />} />
      </Route>

      {/* Pages CRUD legacy Sprint 5 (catalogue formations, fiches formateurs, etc.)
          accessibles depuis liens internes des onglets B2B Sessions */}
      <Route path="/formation/legacy" element={shell('formation', <FormationLayout />)}>
        <Route index element={<FormationDashboardPage />} />
        <Route path="catalogue" element={<CataloguePage />} />
        <Route path="catalogue/:id" element={<FicheFormationPage />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="sessions/:id" element={<SessionDetailPage />} />
        <Route path="inscriptions" element={<InscriptionsPage />} />
        <Route path="formateurs" element={<FormateursPage />} />
        <Route path="evaluations" element={<EvaluationsPage />} />
        <Route path="rapports" element={<RapportsPage />} />
      </Route>
      <Route path="/eval/:token" element={<EvaluationPubliquePage />} />

      {/* Backward compat — /dossiers et /formation-b2b/* et /formation-b2c/* */}
      <Route
        path="/dossiers"
        element={<Navigate to="/formation?segment=b2b&tab=sessions" replace />}
      />
      {/* Backward compat : /dossiers/:id rend la même Vue 360° qu'à /formation/sessions/:id */}
      <Route path="/dossiers/:id" element={shell('formation', <DossierDetailPage />)} />
      <Route
        path="/formation-b2b"
        element={<Navigate to="/formation?segment=b2b&tab=sessions" replace />}
      />
      <Route
        path="/formation-b2b/sessions"
        element={<Navigate to="/formation?segment=b2b&tab=sessions" replace />}
      />
      <Route
        path="/formation-b2b/factures"
        element={<Navigate to="/formation?segment=b2b&tab=factures" replace />}
      />
      <Route
        path="/formation-b2b/dettes"
        element={<Navigate to="/formation?segment=b2b&tab=dettes" replace />}
      />
      <Route
        path="/formation-b2b/roi"
        element={<Navigate to="/formation?segment=b2b&tab=roi" replace />}
      />
      <Route
        path="/formation-b2b/inter"
        element={<Navigate to="/formation?segment=b2b&tab=inter" replace />}
      />
      <Route
        path="/formation-b2c"
        element={<Navigate to="/formation?segment=b2c&tab=sessions" replace />}
      />
      <Route
        path="/formation-b2c/sessions"
        element={<Navigate to="/formation?segment=b2c&tab=sessions" replace />}
      />
      <Route
        path="/formation-b2c/roi"
        element={<Navigate to="/formation?segment=b2c&tab=roi" replace />}
      />
      <Route
        path="/formation-b2c/inter"
        element={<Navigate to="/formation?segment=b2c&tab=inter" replace />}
      />

      <Route path="/marketing" element={shell('marketing', <MarketingB2cPage />)} />
      <Route path="/rh" element={shell('rh', <ModulePlaceholder titleKey="nav.rh" />)} />
      <Route path="/reporting" element={shell('dashboard', <ReportingPage />)} />
      <Route path="/communication" element={shell('marketing', <CommunicationPage />)} />

      <Route path="/parametres" element={shell('parametres', <ParametresLayout />)}>
        <Route index element={<Navigate to="/parametres/organisation" replace />} />
        <Route path="organisation" element={<OrganisationPage />} />
        <Route path="activites" element={<ActivitesGlobalPage />} />
        <Route path="organisation/:entrepriseId/activites" element={<ActivitesPage />} />
        <Route
          path="organisation/:entrepriseId/activites/:activiteId/services"
          element={<ServicesPage />}
        />
        <Route path="tiers" element={<TiersPage />} />
        <Route path="tiers/doublons" element={<DoublonsTiersPage />} />
        <Route path="tiers/:id" element={<TierDetailPage />} />
        <Route path="banques-caisses" element={<BanquesPage />} />
        <Route path="plan-comptable" element={<PlanComptablePage />} />
        <Route path="workflows" element={<WorkflowsPage />} />
        <Route path="utilisateurs" element={<UtilisateursPage />} />
        <Route path="utilisateurs/:id/perimetre" element={<PerimetreUserPage />} />
        <Route path="utilisateurs/:id/permissions" element={<UserPermissionsPage />} />
        <Route path="crm/pipelines" element={<PipelinesAdminPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="backups" element={<BackupsPage />} />
      </Route>

      <Route path="/test-datatable" element={shell('dashboard', <TestDataTablePage />)} />

      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
